import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LoadingIndicator from '../../components/ui/LoadingIndicator';
import RenderConfiguration from './components/RenderConfiguration';
import CreditPreview from './components/CreditPreview';
import RenderQueue from './components/RenderQueue';
import CompletedRenders from './components/CompletedRenders';
import NotificationSettings from './components/NotificationSettings';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { renderService, subscriptionService, activityService } from '../../utils/supabaseQueries';

const VideoRenderingPage = () => {
  const { user, userProfile } = useAuth();
  const [isRendering, setIsRendering] = useState(false);
  const [activeTab, setActiveTab] = useState('configure');
  const [renderProgress, setRenderProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Real data states
  const [queuedRenders, setQueuedRenders] = useState([]);
  const [completedRenders, setCompletedRenders] = useState([]);
  const [currentRender, setCurrentRender] = useState(null);
  const [tabCounts, setTabCounts] = useState({
    queue: 0,
    completed: 0
  });

  // Real-time subscription
  useEffect(() => {
    if (!user) return;

    const subscription = subscriptionService?.subscribeToRenderJobs((payload) => {
      const { eventType, new: newRecord, old: oldRecord } = payload;
      
      if (eventType === 'INSERT') {
        if (newRecord?.status === 'pending' || newRecord?.status === 'processing') {
          setQueuedRenders(prev => [newRecord, ...prev]);
          setTabCounts(prev => ({ ...prev, queue: prev?.queue + 1 }));
        } else if (newRecord?.status === 'completed') {
          setCompletedRenders(prev => [newRecord, ...prev]);
          setTabCounts(prev => ({ ...prev, completed: prev?.completed + 1 }));
        }
      } else if (eventType === 'UPDATE') {
        // Update render status
        if (newRecord?.status === 'completed' && oldRecord?.status !== 'completed') {
          // Move from queue to completed
          setQueuedRenders(prev => prev?.filter(r => r?.id !== newRecord?.id));
          setCompletedRenders(prev => [newRecord, ...prev]);
          setTabCounts(prev => ({ 
            ...prev, 
            queue: Math.max(0, prev?.queue - 1),
            completed: prev?.completed + 1
          }));
          
          if (currentRender?.id === newRecord?.id) {
            setCurrentRender(null);
            setIsRendering(false);
            setActiveTab('completed');
          }
        } else {
          // Update existing render
          setQueuedRenders(prev => 
            prev?.map(r => r?.id === newRecord?.id ? newRecord : r)
          );
          
          if (currentRender?.id === newRecord?.id) {
            setCurrentRender(newRecord);
            setRenderProgress(newRecord?.progress || 0);
          }
        }
      }
    });

    return () => {
      subscriptionService?.unsubscribe(subscription);
    };
  }, [user, currentRender]);

  // Load initial data
  useEffect(() => {
    const loadRenderData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Get render queue
        const { data: queueData, error: queueError } = await renderService?.getRenderQueue();
        if (queueError) {
          setError('Unable to load render queue. Please check your connection and try again.');
          return;
        }

        // Get user's completed renders - we need project renders for current user
        const { data: projectsData, error: projectsError } = await renderService?.getUserProjectRenders();
        if (projectsError) {
          setError('Unable to load project data. Please check your connection and try again.');
          return;
        }

        const userQueuedRenders = queueData?.filter(render => 
          render?.started_by === user?.id || 
          projectsData?.some(project => project?.id === render?.project_id)
        ) || [];

        const userCompletedRenders = projectsData?.flatMap(project => 
          project?.render_jobs?.filter(job => job?.status === 'completed') || []
        ) || [];

        setQueuedRenders(userQueuedRenders);
        setCompletedRenders(userCompletedRenders);
        
        // Set current render if any processing
        const processing = userQueuedRenders?.find(r => r?.status === 'processing');
        if (processing) {
          setCurrentRender(processing);
          setIsRendering(true);
          setRenderProgress(processing?.progress || 0);
        }

        setTabCounts({
          queue: userQueuedRenders?.length || 0,
          completed: userCompletedRenders?.length || 0
        });

      } catch (error) {
        setError('Failed to load rendering data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    loadRenderData();
  }, [user]);

  const tabs = [
    { id: 'configure', label: 'Configure', icon: 'Settings', count: null },
    { id: 'queue', label: 'Queue', icon: 'List', count: tabCounts?.queue },
    { id: 'completed', label: 'Completed', icon: 'Download', count: tabCounts?.completed },
    { id: 'notifications', label: 'Notifications', icon: 'Bell', count: null }
  ];

  const handleStartRender = async (config) => {
    if (!user || !config?.projectId) {
      setError('Please select a project and configure render settings.');
      return;
    }

    try {
      setError(null);
      const { data, error } = await renderService?.createRenderJob({
        projectId: config?.projectId,
        videoFileId: config?.videoFileId,
        name: config?.name || 'Video Render',
        quality: config?.quality || 'medium',
        settings: config?.settings || {}
      });

      if (error) {
        setError(`Failed to start render: ${error?.message}`);
        return;
      }

      // Log activity
      await activityService?.logActivity(config?.projectId, 'render_started', {
        render_id: data?.id,
        render_name: data?.name,
        quality: data?.quality
      });

      setCurrentRender(data);
      setIsRendering(true);
      setRenderProgress(0);
      setActiveTab('queue');
      
    } catch (error) {
      setError('Failed to start render. Please try again.');
    }
  };

  const handleCancelRender = async (renderId) => {
    try {
      setError(null);
      const { error } = await renderService?.updateRenderJob(renderId, {
        status: 'cancelled'
      });

      if (error) {
        setError(`Failed to cancel render: ${error?.message}`);
        return;
      }

      // Update local state
      setQueuedRenders(prev => prev?.filter(r => r?.id !== renderId));
      if (currentRender?.id === renderId) {
        setCurrentRender(null);
        setIsRendering(false);
      }

    } catch (error) {
      setError('Failed to cancel render. Please try again.');
    }
  };

  const handleRetryRender = async (renderId) => {
    try {
      setError(null);
      const { error } = await renderService?.updateRenderJob(renderId, {
        status: 'pending',
        error_message: null
      });

      if (error) {
        setError(`Failed to retry render: ${error?.message}`);
        return;
      }

    } catch (error) {
      setError('Failed to retry render. Please try again.');
    }
  };

  const handleDownload = async (render) => {
    if (!render?.output_path) {
      setError('Download link not available for this render.');
      return;
    }

    try {
      // In a real implementation, you'd handle secure file download from Supabase storage
      // For now, we'll just log the action
      await activityService?.logActivity(render?.project_id, 'render_downloaded', {
        render_id: render?.id,
        file_name: render?.name
      });

      // Mock download - in real app you'd get signed URL from storage console.log('Downloading:', render?.name);
      
    } catch (error) {
      setError('Failed to download file. Please try again.');
    }
  };

  const handleDelete = async (renderId) => {
    try {
      setError(null);
      
      // In a real implementation, you'd also delete the file from storage
      const renderToDelete = completedRenders?.find(r => r?.id === renderId);
      
      if (renderToDelete) {
        await activityService?.logActivity(renderToDelete?.project_id, 'render_deleted', {
          render_id: renderId,
          file_name: renderToDelete?.name
        });
      }

      // Remove from local state (in real app, you'd have a delete API endpoint)
      setCompletedRenders(prev => prev?.filter(r => r?.id !== renderId));
      
    } catch (error) {
      setError('Failed to delete render. Please try again.');
    }
  };

  const handleSaveNotificationSettings = async (settings) => {
    try {
      setError(null);
      // In a real implementation, you'd save to user preferences console.log('Saving notification settings:', settings);
      
    } catch (error) {
      setError('Failed to save notification settings. Please try again.');
    }
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <LoadingIndicator type="spinner" size="lg" />
        </div>
      );
    }

    switch (activeTab) {
      case 'configure':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RenderConfiguration 
              onStartRender={handleStartRender}
              isRendering={isRendering}
            />
            <CreditPreview />
          </div>
        );
      case 'queue':
        return (
          <RenderQueue
            currentRender={currentRender}
            queuedRenders={queuedRenders}
            onCancelRender={handleCancelRender}
            onRetryRender={handleRetryRender}
            loading={loading}
          />
        );
      case 'completed':
        return (
          <CompletedRenders
            renders={completedRenders}
            onDownload={handleDownload}
            onDelete={handleDelete}
            loading={loading}
          />
        );
      case 'notifications':
        return (
          <NotificationSettings
            onSaveSettings={handleSaveNotificationSettings}
          />
        );
      default:
        return null;
    }
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingIndicator type="spinner" size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header userRole={userProfile?.role || "user"} userName={userProfile?.full_name || "User"} />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <Breadcrumb />
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Video Rendering</h1>
                <p className="text-muted-foreground">
                  Generate and download your credit videos with professional formatting
                </p>
              </div>
              
              <div className="flex items-center space-x-3 mt-4 sm:mt-0">
                <Button variant="outline" iconName="HelpCircle">
                  Help
                </Button>
                <Button variant="outline" iconName="History">
                  History
                </Button>
              </div>
            </div>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="bg-error/5 border border-error/20 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-error">{error}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setError(null)}
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>
          )}

          {/* Render Progress Banner */}
          {isRendering && currentRender && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <LoadingIndicator type="spinner" size="sm" showMessage={false} />
                  <div>
                    <h3 className="text-sm font-medium text-foreground">{currentRender?.name}</h3>
                    <p className="text-xs text-muted-foreground">Processing your video render...</p>
                  </div>
                </div>
                <span className="text-sm font-medium text-primary">{Math.round(currentRender?.progress || 0)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${currentRender?.progress || 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="border-b border-border mb-6">
            <nav className="flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                  {tab?.count !== null && tab?.count > 0 && (
                    <span className="bg-muted text-muted-foreground text-xs px-2 py-1 rounded-full">
                      {tab?.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {renderTabContent()}
          </div>

          {/* Quick Actions */}
          <div className="mt-8 p-6 bg-muted/30 rounded-lg border border-border">
            <h3 className="text-sm font-medium text-foreground mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={() => window.location.href = '/credit-creation'}
                fullWidth
              >
                New Project
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                iconName="FolderOpen"
                iconPosition="left"
                onClick={() => window.location.href = '/project-management'}
                fullWidth
              >
                My Projects
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                fullWidth
                disabled={completedRenders?.length === 0}
              >
                Download All
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                iconName="Share2"
                iconPosition="left"
                fullWidth
              >
                Share Link
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VideoRenderingPage;